// ============================================
// ModalReasignar.js
// ============================================
// Cambia el responsable (vendedor_id) del expediente. Cualquier usuario
// logueado puede reasignar. Si el expediente esta cerrado y el usuario
// NO es direccion, se muestra warning bloqueante.

import React, { useEffect, useState } from 'react';
import {
  API_BASE_CLI, FF, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CL, COLOR_BORDE,
  inputStyle, botonSecundario, botonPrimario,
  authHeaders, ShellModal, ToastFlotante, esRolDireccion,
} from './helpersExpedientes';
import { useUsuario } from '../../context/UsuarioContext';

export default function ModalReasignar({ expediente, onCerrar, onReasignado, esCelular }) {
  const { usuario } = useUsuario();
  const direccion = esRolDireccion(usuario);
  const cerrado = expediente?.tipo_cierre != null;
  const bloqueado = cerrado && !direccion;

  const [usuarios, setUsuarios] = useState([]);
  const [vendedorId, setVendedorId] = useState(expediente?.vendedor_id || '');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let activo = true;
    fetch(`${API_BASE_CLI}/api/usuarios`, { headers: authHeaders() })
      .then(r => r.json())
      .then(j => {
        if (!activo) return;
        const lista = Array.isArray(j?.usuarios) ? j.usuarios : [];
        setUsuarios(lista.filter(u => u.activo !== false && u.estado !== 'archivado'));
      })
      .catch(() => {});
    return () => { activo = false; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !enviando) onCerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enviando, onCerrar]);

  const cambio = String(vendedorId || '') !== String(expediente?.vendedor_id || '');
  const valido = !!vendedorId && cambio;

  const guardar = async () => {
    if (!valido || enviando || bloqueado) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades/${expediente.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          vendedor_id: Number(vendedorId),
          usuario_id: usuario?.id || null,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401 || r.status === 403) {
        setError('No tienes permisos para reasignar.');
        return;
      }
      if (!r.ok) throw new Error(data?.error || `Error ${r.status}`);
      setToast({ tipo: 'ok', msg: 'Responsable reasignado.' });
      setTimeout(() => onReasignado(data), 800);
    } catch (e) {
      setError(e.message || 'No se pudo reasignar.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <ShellModal onClose={enviando ? () => {} : onCerrar} ancho={460} esCelular={esCelular}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLOR_OSCURO, fontFamily: FF }}>
              Reasignar responsable
            </h2>
            <div style={{ marginTop: 4, fontSize: 12, color: COLOR_GRIS_CL }}>
              {expediente.folio}
              {expediente.responsable_nombre && (
                <> &middot; Actual: <strong>{expediente.responsable_nombre}</strong></>
              )}
            </div>
          </div>
          <button
            onClick={onCerrar}
            disabled={enviando}
            aria-label="Cerrar"
            style={{
              background: 'transparent', border: 'none',
              cursor: enviando ? 'not-allowed' : 'pointer',
              color: COLOR_GRIS_CL, fontSize: 22, padding: 4,
            }}
          >{'×'}</button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bloqueado && (
            <div style={{
              background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e',
              padding: '10px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            }}>
              Expediente cerrado. Solo direccion puede reasignar.
            </div>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600 }}>Nuevo responsable</span>
            <select
              value={vendedorId}
              onChange={(e) => setVendedorId(e.target.value)}
              disabled={bloqueado || enviando}
              style={inputStyle}
            >
              <option value="">-- selecciona un usuario --</option>
              {usuarios.map(u => (
                <option key={u.id} value={u.id}>
                  {u.nombre}{u.rol ? ` (${u.rol})` : ''}
                </option>
              ))}
            </select>
          </label>
          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: '8px 12px', borderRadius: 8, fontSize: 12,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          padding: 16, borderTop: `1px solid ${COLOR_BORDE}`, background: '#fafafa',
          display: 'flex', justifyContent: 'space-between', gap: 10,
        }}>
          <button onClick={onCerrar} disabled={enviando} style={botonSecundario}>Cancelar</button>
          <button
            onClick={guardar}
            disabled={!valido || enviando || bloqueado}
            style={{
              ...botonPrimario,
              opacity: (!valido || enviando || bloqueado) ? 0.5 : 1,
              cursor: (!valido || enviando || bloqueado) ? 'not-allowed' : 'pointer',
            }}
          >{enviando ? 'Guardando...' : 'Reasignar'}</button>
        </div>
      </ShellModal>
      {toast && <ToastFlotante tipo={toast.tipo}>{toast.msg}</ToastFlotante>}
    </>
  );
}
